import {onUnmounted, watchEffect} from "vue";
import {useAiAssistantStore} from "@/stores/aiAssistant.store";
import {AssistantScreen} from "@/ai-assistant/interfaces";

/**
 * Owners are told apart by a counter rather than by the screen they describe:
 * two screens may carry the same label, and a screen that unmounts must only
 * clear the context if it is still the one holding it.
 */
let nextOwner = 0;

/**
 * Declare what the pharmacist has in front of them while this screen is
 * mounted.
 *
 * The context follows the screen: the getter is watched, so moving to the next
 * week re-declares it, and the question asked afterwards leaves with the week
 * actually on display. Nothing is sent here — the drawer reads the context at
 * the moment a question is sent, which is the only moment it is true.
 *
 * On leaving, the context is released only if this screen still holds it. Vue
 * mounts the next screen before unmounting the previous one on some
 * transitions, and a blind reset there would erase the context the arriving
 * screen has just declared.
 *
 * @param screen What to say about this screen, re-read whenever it changes.
 */
const useAssistantScreen = (screen: () => AssistantScreen | null): void => {
    const store = useAiAssistantStore();
    const owner = ++nextOwner;

    watchEffect(() => store.setScreen(owner, screen()));

    onUnmounted(() => store.releaseScreen(owner));
};

export default useAssistantScreen;
